import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useBilling from "../../services/Billing/useBilling";
import { GlobalContext } from "../../Context/GlobalContext";
import LoadingScreen from "../../utils/LoadingScreen";
import NoIndex from "../../components/NoIndex";
import { RotateCcw } from "lucide-react";

const ITEMS_PER_PAGE = 10;

const Ledger = () => {
  const { userID } = useContext(GlobalContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [interval] = useState("30");
  const navigate = useNavigate();

  const {
    ledgerData,
    loading,
    startDateCurrent,
    endDateCurrent,
    setStartDateCurrent,
    setEndDateCurrent,
    fetchLedgerData,
  } = useBilling(userID, interval);

  const totalPages = Math.ceil(ledgerData.length / ITEMS_PER_PAGE);
  const currentTransactions = ledgerData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleSearch = () => {
    setCurrentPage(1);
    fetchLedgerData();
  };

  const handleRefresh = () => {
    // setCurrentPage(1);   // optional (page reset)
    fetchLedgerData();
  };

  // Auto fetch on page load
  useEffect(() => {
    fetchLedgerData();
  }, []);

  const prevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const nextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const paginate = (page) => setCurrentPage(page);

  return (
    <div className="min-h-screen p-6">
      <NoIndex />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 montserrat-font-2">
        {/* ── Header: Title left | Dates + Back right ── */}
        <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
          <h3 className="text-xl font-bold text-gray-800 montserrat-font-1">
            Transaction Ledger
          </h3>

          <div className="flex items-end gap-4 flex-wrap montserrat-font-1">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 ">
                Start Date
              </label>
              <input
                type="date"
                value={startDateCurrent}
                onChange={(e) => setStartDateCurrent(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 outline-none"
              />
            </div>
            <div className="montserrat-font-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDateCurrent}
                onChange={(e) => setEndDateCurrent(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 outline-none"
              />
            </div>
            <button
              onClick={handleSearch}
              className="bg-green-600 text-white px-5 py-2.5 rounded-md text-sm font-semibold montserrat-font-1"
            >
              Apply
            </button>

            <button
              onClick={handleRefresh}
              className="border border-gray-300 text-white px-4 py-2.5 rounded-md text-sm font-semibold bg-blue-500 flex items-center gap-2 montserrat-font-1"
            >
              <RotateCcw className="w-4 h-4" />
              Refresh
            </button>

            <button
              onClick={() => navigate("/Billing")}
              className="border border-gray-300 text-gray-600 px-4 py-2 rounded-md text-sm font-semibold montserrat-font-1"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* ── Loader ── */}
        {loading ? (
          <div className="flex justify-center py-6">
            <LoadingScreen text="Updating....." />
          </div>
        ) : currentTransactions.length === 0 ? (
          <div className="text-center text-gray-400 py-8 montserrat-font-1">
            No data found
          </div>
        ) : (
          <>
            {/* ── Transaction Cards ── */}
            <div className="space-y-3">
              {currentTransactions.map((txn, index) => (
                <div
                  key={index}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4"
                >
                  {/* Row 1: Date | Particulars */}
                  <div className="flex justify-between flex-wrap gap-2 montserrat-font-1">
                    <div className="text-sm text-gray-800">
                      <strong>Transaction Date:</strong>{" "}
                      {new Date(txn.transactionDate).toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-800 montserrat-font-1">
                      <strong>Particulars:</strong> {txn.narraton}
                    </div>
                  </div>

                  {/* Row 2: Type | Amount */}
                  <div className="flex justify-between mt-2 montserrat-font-1">
                    <div
                      className={`text-sm font-bold ${txn.transactionType === "credit" ? "text-green-600" : "text-red-500"}`}
                    >
                      <strong>Type:</strong>{" "}
                      {txn.transactionType.charAt(0).toUpperCase() +
                        txn.transactionType.slice(1)}
                    </div>
                    <div
                      className={`text-sm font-bold ${txn.transactionType === "credit" ? "text-green-600" : "text-red-500"}`}
                    >
                      <strong>Amount:</strong> ₹{" "}
                      {parseFloat(txn.transactionAmount).toFixed(2)}
                    </div>
                  </div>

                  {/* Row 3: Opening | Closing Balance */}
                  <div className="flex justify-between mt-2 text-sm text-gray-700 montserrat-font-1">
                    <div>
                      <strong>Opening Balance:</strong> ₹{" "}
                      {parseFloat(txn.openingBalance).toFixed(2)}
                    </div>
                    <div>
                      <strong>Closing Balance:</strong> ₹{" "}
                      {parseFloat(txn.closingBalance).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Pagination ── */}
            {ledgerData.length > 0 && (
              <div className="flex justify-center items-center mt-6 flex-wrap gap-2">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-md border border-blue-500 text-blue-500 text-sm hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed montserrat-font-1"
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => paginate(page)}
                      className={`px-3 py-2 rounded-md text-sm font-medium border ${
                        currentPage === page
                          ? "bg-blue-500 text-white border-blue-500"
                          : "border-blue-500 text-blue-500 hover:bg-blue-50"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-md border border-blue-500 text-blue-500 text-sm hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed montserrat-font-1"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Ledger;
